import React, { useEffect, useState, useRef } from "react";
import images from "../../assets/images";
import { AgGridReact } from "ag-grid-react";
import { ColDef, SelectionChangedEvent } from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "../../styles/ag-grid-custom.css";
// import "./TenantManagement.css";
import Modal from "../../components/modal/Modal";
import { createTenant, getAllTenants, deleteTenant } from "../../service/api";
import { toast } from "react-toastify";
import { PageLoader } from "../../components/loader/Loader";

interface Tenant {
  _id?: string;
  tenant_name: string;
  org_name: string;
  org_admin_name: string;
  org_admin_email: string;
  org_admin_phone: string;
  status: string;
  database_name?: string;
  createdOn?: string;
}

const TenantManagement = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [selectedRows, setSelectedRows] = useState<Tenant[]>([]);
  const gridRef = useRef<AgGridReact<Tenant>>(null);
  const [tenantToDelete, setTenantToDelete] = useState<Tenant | null>(null);
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [rowData, setRowData] = useState<Tenant[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [formData, setFormData] = useState({
    tenant_name: "",
    org_name: "",
    org_admin_name: "",
    org_admin_email: "",
    org_admin_phone: "",
  });

  useEffect(() => {
    fetchTenants();
  }, []);

  const fetchTenants = async () => {
    try {
      setIsLoading(true);
      const response = await getAllTenants();
      if (response.data && Array.isArray(response.data.message)) {
        setRowData(response.data.message);
      }
    } catch (error) {
      console.error("Error fetching tenants", error);
      toast.error("Failed to fetch tenants");
    } finally {
      setIsLoading(false);
    }
  };

  const handleChanges = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setIsModalOpen(false);
    setIsCreating(true);

    try {
      await createTenant(formData);
      toast.success("Tenant created successfully");
      setIsModalOpen(false);
      setFormData({
        tenant_name: "",
        org_name: "",
        org_admin_name: "",
        org_admin_email: "",
        org_admin_phone: "",
      });
      fetchTenants();
    } catch (error: any) {
      console.error("Error creating tenant", error);
      const errorMessage =
        error.response?.data?.message || "Failed to create tenant";
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
      setIsCreating(false);
    }
  };

  const openDeleteConfirmation = (tenant: Tenant) => {
    setTenantToDelete(tenant);
    setIsDeleteModalOpen(true);
  };

  const confirmBulkDeleteTenants = async () => {
    const ids = selectedRows
      .map((t) => t._id)
      .filter((id): id is string => Boolean(id));
    if (ids.length === 0) return;

    try {
      setIsBulkDeleting(true);
      const results = await Promise.allSettled(
        ids.map((id) => deleteTenant(id)),
      );
      const failed = results.filter((r) => r.status === "rejected").length;
      const succeeded = ids.length - failed;

      if (failed === 0) {
        toast.success(
          `${succeeded} tenant${succeeded === 1 ? "" : "s"} deleted successfully`,
        );
      } else {
        toast.error(
          `${succeeded} deleted, ${failed} failed. Check the console for details.`,
        );
      }

      setIsBulkDeleteModalOpen(false);
      gridRef.current?.api.deselectAll();
      setSelectedRows([]);
      fetchTenants();
    } catch (error: any) {
      console.error("Error bulk deleting tenants", error);
      toast.error("Failed to delete selected tenants");
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const confirmDeleteTenant = async () => {
    if (!tenantToDelete?._id) return;

    try {
      setIsDeleting(true);
      const response = await deleteTenant(tenantToDelete._id);
      toast.success(response.data.resMessage || "Tenant deleted successfully");
      setIsDeleteModalOpen(false);
      setTenantToDelete(null);
      fetchTenants();
    } catch (error: any) {
      console.error("Error deleting tenant", error);
      const errorMessage =
        error.response?.data?.message || "Failed to delete tenant";
      toast.error(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleStatusToggle = (index: number) => {
    setRowData((prevData) =>
      prevData.map((row, i) =>
        i === index
          ? { ...row, status: row.status === "Active" ? "Inactive" : "Active" }
          : row,
      ),
    );
  };

  const handleViewTenant = (tenant: Tenant) => {
    setSelectedTenant(tenant);
    setIsViewModalOpen(true);
  };

  const columnDefs: ColDef<Tenant>[] = [
    { headerName: "Tenant Name", field: "tenant_name", flex: 1 },
    { headerName: "Organisation Name", field: "org_name", flex: 1 },
    { headerName: "Organisation Admin Name", field: "org_admin_name", flex: 1 },
    {
      headerName: "Organisation Admin Email",
      field: "org_admin_email",
      flex: 1,
    },
    {
      headerName: "Organisation Admin Phone",
      field: "org_admin_phone",
      flex: 1,
    },
    {
      headerName: "Status",
      field: "status",
      flex: 1,
      cellRenderer: (params: any) => {
        const index = params.node._id;
        return (
          <div className="flex items-center justify-center h-full">
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={params.value === "active"}
                onChange={() => handleStatusToggle(index)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-green-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
              <span className="ml-2 text-sm font-medium text-gray-900">
                {params.value.charAt(0).toUpperCase() + params.value.slice(1)}
              </span>
            </label>
          </div>
        );
      },
    },
    {
      headerName: "Actions",
      flex: 1,
      cellRenderer: (params: any) => {
        const tenantId = params.data._id;
        const tenantData = params.data;
        return (
          <div className="flex justify-center">
            <button onClick={() => handleViewTenant(tenantData)}>
              <img
                src={images.viewIcon}
                alt="view-icon"
                className="iconTd w-5 h-5"
              />
            </button>
            <button
              onClick={() => openDeleteConfirmation(tenantData)}
              disabled={isLoading || isDeleting}
              className="text-red-600 hover:text-red-800 disabled:opacity-50"
              title="Delete Tenant"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <>
      {(isCreating || isLoading) && <PageLoader />}
      <div className="px-2 py-4 mt-14 ml-2 min-h-100vh">
        <div className="flex justify-between items-center">
          <h2 className="mb-3">Tenant Management</h2>

          <div className="flex items-center gap-3">
            {selectedRows.length > 0 && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-teal-50 border border-[#0fa9a6]/30 text-[#0c7f7d] text-xs font-semibold rounded-full shadow-sm animate-tenantFadeIn">
                <span className="w-2 h-2 rounded-full bg-[#0fa9a6] animate-pulse"></span>
                <span>
                  {selectedRows.length}{" "}
                  {selectedRows.length === 1 ? "tenant" : "tenants"} selected
                </span>
                <button
                  type="button"
                  onClick={() => {
                    gridRef.current?.api.deselectAll();
                    setSelectedRows([]);
                  }}
                  className="ml-1 text-gray-400 hover:text-gray-700 font-bold transition-colors cursor-pointer"
                  title="Clear selection"
                >
                  ✕
                </button>
              </div>
            )}

            <button
              className={`flex items-center justify-center font-semibold gap-2 px-4 py-2 rounded-full text-sm transition-all duration-200 ${
                selectedRows.length === 0
                  ? "bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed opacity-60"
                  : "bg-red-600 hover:bg-red-700 text-white shadow-sm hover:shadow active:scale-95 cursor-pointer"
              }`}
              onClick={() => setIsBulkDeleteModalOpen(true)}
              disabled={
                selectedRows.length === 0 || isBulkDeleting || isDeleting
              }
              title="Delete all selected tenants"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
              DELETE SELECTED
              {selectedRows.length > 0 ? ` (${selectedRows.length})` : ""}
            </button>

            <button
              className="flex items-center justify-center font-semibold gap-2 bg-[#0fa9a6] hover:bg-[#0c8f8c] text-white px-4 py-2 rounded-full text-sm shadow-sm transition-all duration-200 active:scale-95 cursor-pointer"
              onClick={() => setIsModalOpen(true)}
            >
              <img src={images.createUserImg} width={18} alt="create-tenant" />
              CREATE TENANT
            </button>
          </div>
        </div>
        {/*         <div className="input-affix w-25 mb-3">
          <i className="prefix-icon anticon anticon-search"></i>
          <input
            type="text"
            className="form-control"
            placeholder="Search by tenant name..."
          />
        </div> */}

        <div className="tableBlock w-full">
          <div className="card">
            <div className="ag-theme-alpine tenant-management-grid">
              <AgGridReact<Tenant>
                ref={gridRef}
                rowData={rowData}
                columnDefs={columnDefs}
                rowSelection={{
                  mode: "multiRow",
                  checkboxes: true,
                  headerCheckbox: true,
                  enableClickSelection: false,
                }}
                selectionColumnDef={{
                  width: 52,
                  minWidth: 52,
                  maxWidth: 52,
                  resizable: false,
                  sortable: false,
                  suppressHeaderMenuButton: true,
                  pinned: "left",
                  cellClass: "tenant-selection-cell",
                  headerClass: "tenant-selection-header",
                }}
                getRowId={(params) => String(params.data._id)}
                onSelectionChanged={(event: SelectionChangedEvent<Tenant>) =>
                  setSelectedRows(event.api.getSelectedRows())
                }
                pagination={true}
                paginationPageSize={10}
                paginationPageSizeSelector={[5, 10, 20]}
                domLayout="autoHeight"
                headerHeight={44}
                rowHeight={46}
              />
            </div>
          </div>
        </div>
      </div>
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        closeButton={false}
        modalClass={"bg-white w-full max-w-4xl rounded-xl shadow-lg relativee"}
      >
        {
          <>
            <div className="pt-6">
              <h2 className="text-xl font-semibold text-gray-800 border-b px-6 pb-2 mb-4">
                CREATE TENANT
              </h2>

              <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-3 gap-4 px-6 pb-6">
                  {[
                    ["Tenant Name", "tenant_name"],
                    ["Organisation Name", "org_name"],
                    ["Organisation Admin Name", "org_admin_name"],
                    ["Organisation Admin Email", "org_admin_email"],
                    ["Organisation Admin Phone", "org_admin_phone"],
                  ].map(([label, name]) => {
                    return (
                      <div key={name} className="form-group">
                        <label className="text-sm block mb-1">{label}</label>
                        <input
                          type="text"
                          name={name}
                          value={formData[name as keyof typeof formData]}
                          onChange={handleChanges}
                          className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#375998]"
                          placeholder={`Enter ${label.toLowerCase()}`}
                          disabled={isLoading}
                        />
                      </div>
                    );
                  })}
                </div>
                <div className="flex justify-end gap-3 mt-4 p-2 bg-[#5A7192] w-full rounded-b-lg">
                  <button
                    onClick={() => setIsModalOpen(false)}
                    type="button"
                    className="bg-[#8497B0]  text-white px-4 py-2 rounded-full hover:opacity-90"
                    disabled={isCreating}
                  >
                    CANCEL
                  </button>
                  <button
                    type="submit"
                    className="bg-[#00A8A8]  text-white px-4 py-2 rounded-full hover:opacity-90"
                    disabled={isCreating}
                  >
                    SUBMIT
                  </button>
                </div>
              </form>
            </div>
          </>
        }
      </Modal>

      <Modal
        isOpen={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        closeButton={false}
        modalClass={"bg-white w-full max-w-4xl rounded-xl shadow-lg relative"}
      >
        <div className="pt-6">
          <h2 className="text-xl font-semibold text-gray-800 border-b px-6 pb-2 mb-4">
            VIEW TENANT
          </h2>

          {selectedTenant && (
            <>
              <div className="grid grid-cols-3 gap-4 px-6 pb-6">
                {[
                  { label: "Tenant Name", value: selectedTenant.tenant_name },
                  {
                    label: "Organisation Name",
                    value: selectedTenant.org_name,
                  },
                  {
                    label: "Organisation Admin Name",
                    value: selectedTenant.org_admin_name,
                  },
                  {
                    label: "Organisation Admin Email",
                    value: selectedTenant.org_admin_email,
                  },
                  {
                    label: "Organisation Admin Phone",
                    value: selectedTenant.org_admin_phone,
                  },
                  {
                    label: "Database Name",
                    value: selectedTenant.database_name,
                  },
                  { label: "Status", value: selectedTenant.status },
                  {
                    label: "Database created on",
                    value: selectedTenant.createdOn,
                  },
                ].map((field, index) => (
                  <div key={index} className="form-group">
                    <label className="text-sm font-medium block mb-1 text-gray-700">
                      {field.label}
                    </label>
                    <div className="w-full border border-gray-200 rounded px-3 py-2 bg-gray-50">
                      {field.value || "N/A"}
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex justify-end gap-3 mt-6 p-2 bg-[#5A7192] w-full rounded-b-lg">
                <button
                  onClick={() => setIsViewModalOpen(false)}
                  type="button"
                  className="bg-[#8497B0] text-white px-4 py-2 rounded-full hover:opacity-90"
                >
                  CLOSE
                </button>
              </div>
            </>
          )}
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => !isDeleting && setIsDeleteModalOpen(false)}
        closeButton={false}
        modalClass={"bg-white w-full max-w-md rounded-xl shadow-lg relative"}
      >
        <div className="pt-6">
          <div className="flex items-center gap-3 px-6 pb-3 border-b">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center text-red-600 flex-shrink-0">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-800">
                Confirm Deletion
              </h2>
              <p className="text-xs text-gray-500">This action is permanent</p>
            </div>
          </div>

          <div className="px-6 py-4">
            <p className="text-sm text-gray-600 mb-3">
              Are you sure you want to delete this tenant?
            </p>
            {tenantToDelete && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm space-y-1">
                <p className="text-gray-700">
                  <span className="font-semibold text-gray-900">
                    Tenant Name:
                  </span>{" "}
                  {tenantToDelete.tenant_name}
                </p>
                <p className="text-gray-700">
                  <span className="font-semibold text-gray-900">
                    Organisation:
                  </span>{" "}
                  {tenantToDelete.org_name}
                </p>
                <p className="text-xs text-red-700 pt-2 border-t border-red-200 font-medium">
                  ⚠️ All tenant database data, users, and credentials will be
                  permanently removed.
                </p>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 p-3 bg-gray-50 border-t w-full rounded-b-xl">
            <button
              onClick={() => {
                if (!isDeleting) {
                  setIsDeleteModalOpen(false);
                  setTenantToDelete(null);
                }
              }}
              type="button"
              className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded-full text-sm font-medium transition"
              disabled={isDeleting}
            >
              Cancel
            </button>
            <button
              onClick={confirmDeleteTenant}
              type="button"
              className="bg-red-600 hover:bg-red-700 text-white px-5 py-2 rounded-full text-sm font-semibold transition disabled:opacity-50"
              disabled={isDeleting}
            >
              {isDeleting ? "Deleting..." : "Delete Tenant"}
            </button>
          </div>
        </div>
      </Modal>

      {/* Bulk Delete Confirmation Modal */}
      <Modal
        isOpen={isBulkDeleteModalOpen}
        onClose={() => !isBulkDeleting && setIsBulkDeleteModalOpen(false)}
        closeButton={false}
        modalClass={"bg-white w-full max-w-md rounded-xl shadow-lg relative"}
      >
        <div className="pt-6">
          <div className="flex items-center gap-3 px-6 pb-3 border-b">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center text-red-600 flex-shrink-0">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-800">
                Confirm Bulk Deletion
              </h2>
              <p className="text-xs text-gray-500">This action is permanent</p>
            </div>
          </div>

          <div className="px-6 py-4">
            <p className="text-sm text-gray-600 mb-3">
              Are you sure you want to delete{" "}
              <span className="font-semibold text-gray-900">
                {selectedRows.length} tenant
                {selectedRows.length === 1 ? "" : "s"}
              </span>
              ?
            </p>
            {selectedRows.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm space-y-1">
                <ul className="list-disc list-inside text-gray-700 max-h-40 overflow-y-auto">
                  {selectedRows.map((tenant) => (
                    <li key={tenant._id}>{tenant.tenant_name}</li>
                  ))}
                </ul>
                <p className="text-xs text-red-700 pt-2 border-t border-red-200 font-medium">
                  ⚠️ All tenant database data, users, and credentials will be
                  permanently removed.
                </p>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 p-3 bg-gray-50 border-t w-full rounded-b-xl">
            <button
              onClick={() => {
                if (!isBulkDeleting) {
                  setIsBulkDeleteModalOpen(false);
                }
              }}
              type="button"
              className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded-full text-sm font-medium transition"
              disabled={isBulkDeleting}
            >
              Cancel
            </button>
            <button
              onClick={confirmBulkDeleteTenants}
              type="button"
              className="bg-red-600 hover:bg-red-700 text-white px-5 py-2 rounded-full text-sm font-semibold transition disabled:opacity-50"
              disabled={isBulkDeleting}
            >
              {isBulkDeleting
                ? "Deleting..."
                : `Delete ${selectedRows.length} Tenant${selectedRows.length === 1 ? "" : "s"}`}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default TenantManagement;
